import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Container,
  Grid,
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import EsLogo from 'views/common/components/UI/Icons/EsLogo';
import GetStartedBtn from 'views/RentMyMotorcycleView/components/GetStartedBtn';
import blur from 'public/imgs/1x1.png';

const PREFIX = 'WelcomeBanner';

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  Container: `${PREFIX}-Container`,
  headerImgBox: `${PREFIX}-headerImgBox`,
  headerImg: `${PREFIX}-headerImg`,
  welcomeTxt: `${PREFIX}-welcomeTxt`,
  mainTitle: `${PREFIX}-mainTitle`,
  subTxt: `${PREFIX}-subTxt`,
  amount: `${PREFIX}-amount`,
  getStartedBtn: `${PREFIX}-getStartedBtn`,
  dialog: `${PREFIX}-dialog`,
  closeButton: `${PREFIX}-closeButton`,
  closeIcn: `${PREFIX}-closeIcn`,
  dialogContent: `${PREFIX}-dialogContent`,
  dlEsLogo: `${PREFIX}-dlEsLogo`,
  dlLogoTxt: `${PREFIX}-dlLogoTxt`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.mainContainer}`]: {
    position: 'relative',
    [theme.breakpoints.up('lg')]: {
      height: theme.spacing(52),
    },
  },

  [`& .${classes.Container}`]: {
    maxWidth: theme.spacing(166.25),
  },

  [`& .${classes.headerImgBox}`]: {
    height: theme.spacing(34),
    overflow: 'hidden',
    position: 'relative',
    [theme.breakpoints.only('md')]: {
      height: theme.spacing(48),
    },
    [theme.breakpoints.up('lg')]: {
      position: 'absolute',
      zIndex: 0,
      height: theme.spacing(48),
      top: theme.spacing(2.5),
      right: 0,
      width: '46%',
      borderRadius: '35px 0 0 35px',
    },
  },

  [`& .${classes.headerImg}`]: {
    height: '100%  !important',
    position: 'absolute',
    zIndex: 0,
    top: '0px !important',
    left: '0px !important',
    right: 'auto !important',
    bottom: 'auto !important',
    width: 'auto !important',
    minWidth: '100% !important',
    maxHeight: 'none !important',
    [theme.breakpoints.only('md')]: {
      width: '100% !important',
      height: 'auto !important',
      minHeight: '100% !important',
      top: `${theme.spacing(-7)} !important`,
    },
    [theme.breakpoints.up('lg')]: {
      width: '100% !important',
      height: 'auto !important',
      minHeight: '100% !important',
      margin: '0 !important',
      top: '50% !important',
      left: '50% !important',
      transform: 'translate(-50%, -50%)',
    },
  },

  [`& .${classes.welcomeTxt}`]: {
    marginTop: theme.spacing(4),
    [theme.breakpoints.only('sm')]: {
      textAlign: 'center',
    },
    [theme.breakpoints.only('md')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      marginTop: 0,
      maxWidth: '85%',
    },
  },

  [`& .${classes.mainTitle}`]: {
    fontSize: theme.typography.pxToRem(32),
    marginBottom: theme.spacing(3),
    fontWeight: 'bolder',
    '& span': {
      display: 'block',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(36),
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      fontSize: theme.typography.pxToRem(48),
      marginBottom: theme.spacing(4),
    },
  },

  [`& .${classes.subTxt}`]: {
    fontSize: theme.typography.pxToRem(22),
    fontWeight: '300',
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(24),
    },
    [theme.breakpoints.up('lg')]: {
      fontSize: theme.typography.pxToRem(30),
    },
  },

  [`& .${classes.amount}`]: {
    color: theme.palette.common.black,
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(22),
    cursor: 'pointer',
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(24),
    },
    [theme.breakpoints.up('lg')]: {
      fontSize: theme.typography.pxToRem(30),
    },
  },

  [`& .${classes.getStartedBtn}`]: {
    marginTop: theme.spacing(4.5),
    fontSize: theme.typography.pxToRem(16),
    fontWeight: '500',
    borderRadius: 10,
    padding: theme.spacing(0.75, 2),
    width: '100%',
    whiteSpace: 'nowrap',
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(20),
      padding: theme.spacing(1, 8),
      marginTop: theme.spacing(5.5),
      width: 'auto',
    },
  },

  [`& .${classes.dialog}`]: {
    borderRadius: 15,
    maxWidth: theme.spacing(86.25),
    [theme.breakpoints.only('sm')]: {
      margin: theme.spacing(1.5),
    },
  },

  [`& .${classes.closeButton}`]: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },

  [`& .${classes.closeIcn}`]: {
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(36),
    },
  },

  [`& .${classes.dialogContent}`]: {
    textAlign: 'center',
    padding: theme.spacing(6, 0, 5),
    fontSize: theme.typography.pxToRem(16),
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(24),
      padding: theme.spacing(8.5, 5, 6.25),
      lineHeight: theme.typography.pxToRem(30),
    },
  },

  [`& .${classes.dlEsLogo}`]: {
    height: theme.spacing(6.25),
    padding: theme.spacing(0, 4),
    marginTop: theme.spacing(4.5),
    display: 'flex',
    '& svg': {
      height: theme.typography.pxToRem(28),
      width: theme.typography.pxToRem(270),
      display: 'block',
      maxHeight: theme.spacing(5),
      margin: 'auto',
      marginTop: theme.spacing(2),
    },
  },

  [`& .${classes.dlLogoTxt}`]: {
    fontWeight: 'bold',
    marginTop: theme.spacing(1.5),
  },
}));

const WelcomeBanner = () => {
  const { t } = useTranslation('fe_er_rent_my_motorcycle');

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Root>
      <Box className={classes.mainContainer}>
        <Box className={classes.headerImgBox}>
          <Image
            className={classes.headerImg}
            src="/imgs/list-your-bike/how_renting.webp"
            alt={t('fe_er_rent_my_motorcycle:rent_your_motorcycle')}
            layout="fill"
            unoptimized
            priority
            placeholder="blur"
            blurDataURL={blur.src}
          />
        </Box>

        <Container className={classes.Container}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6} className={classes.contentGrid}>
              <Box className={classes.welcomeTxt}>
                <Typography variant="h1" classes={{ root: classes.mainTitle }}>
                  {t('fe_er_rent_my_motorcycle:rent_your_motorcycle')}
                  <Typography variant="h2" component="span">
                    {t('fe_er_rent_my_motorcycle:on_eagleshare')}
                  </Typography>
                </Typography>
                <Typography classes={{ root: classes.subTitle }} variant="h3">
                  <Typography
                    component="span"
                    classes={{ root: classes.subTxt }}>
                    {t('fe_er_rent_my_motorcycle:earn_up_to_1')}
                  </Typography>
                  <Typography
                    component="a"
                    classes={{ root: classes.amount }}
                    onClick={handleClickOpen}>
                    {t('fe_er_rent_my_motorcycle:earn_up_to_2')}
                  </Typography>
                  <Typography
                    component="span"
                    classes={{ root: classes.subTxt }}
                    dangerouslySetInnerHTML={{
                      __html: t('fe_er_rent_my_motorcycle:earn_up_to_3'),
                    }}
                  />
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection={{ xs: 'column', md: 'row' }}>
                  <GetStartedBtn t={t} styleClass={classes.getStartedBtn} />
                  <Box className={classes.dlEsLogo}>
                    <EsLogo classes={{ root: classes.esLogo }} />
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        classes={{ paper: classes.dialog }}>
        <DialogContent>
          <IconButton
            className={classes.closeButton}
            onClick={handleClose}
            size="large">
            <CloseIcon className={classes.closeIcn} />
          </IconButton>
          <Typography classes={{ root: classes.dialogContent }}>
            {t('fe_er_rent_my_motorcycle:earn_details')}
          </Typography>
        </DialogContent>
      </Dialog>
    </Root>
  );
};

export default WelcomeBanner;
